import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Save, User, Building, LoaderCircle } from 'lucide-react';
import { Button } from '@univdiam/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@univdiam/ui/components/card';
import { Input } from '@univdiam/ui/components/input';
import { Separator } from '@univdiam/ui/components/separator';
import MediaUploader from '../MediaUploader';
import type { Admin, Customer } from '@univdiam/ui/type';
import axios from 'axios';
import ErrorComponent from '../ErrorComponent';
import Loader from '../Loader';
import { normalizeUrl } from '@univdiam/ui/lib/UrlNormalize';

export interface ProfileDetails {
    profilePicture?: string;
    fullName: string;
    email: string;
    phoneNumber: string;
}

export interface BusinessDetails {
    companyLogo?: string;
    businessName?: string;
    phoneNumber?: string;
    website?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
}

export interface AccountData {
    profile: ProfileDetails;
    business: BusinessDetails;
}

interface AccountSettingsProps {
    user: Customer | Admin,
    handleSendToUpload: (files: File[]) => Promise<string[]>;
    accountData?: AccountData;
    onSave?: (data: AccountData) => void;
    onCancel?: () => void;
    isLoading?: boolean;
    mediaLoading?: boolean;
    error?: Error | null;
    isError: boolean
    isAdmin: boolean | undefined

}

export const accountSchema = z.object({
    profile: z.object({
        profilePicture: z.string().optional(),
        fullName: z.string().min(1, 'Full name is required').max(100, 'Full name cannot exceed 100 characters'),
        email: z.string().email('Invalid email address').max(255, 'Email cannot exceed 255 characters'),
        phoneNumber: z.string().max(20, 'Phone number cannot exceed 20 characters'),
    }),
    business: z.object({
        companyLogo: z.string().optional(),
        businessName: z.string().optional().refine(val => !val || val.length <= 100, { message: 'Business name cannot exceed 100 characters' }),
        phoneNumber: z.string().optional().refine(val => !val || val.length <= 20, { message: 'Phone number cannot exceed 20 characters' }),
        website: z.string().max(255, 'Website URL cannot exceed 255 characters'),
        addressLine1: z.string().optional().refine(val => !val || val.length <= 255, { message: 'Address cannot exceed 255 characters' }),
        addressLine2: z.string().optional().refine(val => !val || val.length <= 255, { message: 'Address cannot exceed 255 characters' }),
        city: z.string().optional().refine(val => !val || val.length <= 100, { message: 'City cannot exceed 100 characters' }),
        state: z.string().optional().refine(val => !val || val.length <= 100, { message: 'State cannot exceed 100 characters' }),
        zipCode: z.string().optional().refine(val => !val || /^\d+$/.test(val), { message: 'Zip code must contain only numbers' }).refine(val => !val || val.length <= 10, { message: 'Zip code cannot exceed 10 characters' }),
    }),
});

export type AccountFormData = z.infer<typeof accountSchema>;

export function AccountSettings({
    user,
    handleSendToUpload,
    accountData,
    onSave,
    onCancel,
    isLoading = false,
    mediaLoading = false,
    error = null,
    isError,
    isAdmin

}: AccountSettingsProps) {
    const isAxiosError = axios.isAxiosError(error)
    const [isEditMode, setIsEditMode] = React.useState(false);

    // Store original values from user prop
    const originalValues = React.useMemo(() => ({
        profile: {
            profilePicture: user?.profile_pic || '',
            fullName: user?.full_name || '',
            email: user?.email || '',
            phoneNumber: user?.phone_number || '',
        },
        business: {
            companyLogo: (user as Customer)?.company_logo || '',
            businessName: (user as Customer)?.business_name || '',
            phoneNumber: (user as Customer)?.business_phone_number || '',
            website: (user as Customer)?.website || '',
            addressLine1: (user as Customer)?.address_line_1 || '',
            addressLine2: (user as Customer)?.address_line_2 || '',
            city: (user as Customer)?.city || '',
            state: (user as Customer)?.state || '',
            zipCode: (user as Customer)?.zip_code || '',
        },
    }), [user]);

    const {
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors }
    } = useForm<AccountFormData>({
        resolver: zodResolver(accountSchema),
        defaultValues: originalValues,
    });

    const formData = watch();

    React.useEffect(() => {
        if (accountData) {
            reset(accountData);
        }
    }, [accountData, reset]);

    const onSubmit = (data: AccountFormData) => {
        if (onSave) {
            onSave(data);
            setIsEditMode(false);
        }

    };

    const handleCancel = () => {
        // Reset to original user data, not accountData
        reset(originalValues);
        if (onCancel) {
            onCancel();
        }
        setIsEditMode(false);
    };



    // if (isSelfFetching) {
    //     return <Loader />
    // }

    return (
        <div className="p-3 md:p-6 space-y-4 md:space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-2 bg-white px-3 md:px-[16px] py-[10px] md:py-[12px] rounded-[8px] sticky top-0 md:top-6 z-10">
                <h1 className="text-[13px] md:text-[14px] font-semibold text-black">Account Settings</h1>
                {!isEditMode ? (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsEditMode(true)}
                        className="caption cusrsor-pointer"
                    >
                        Edit Account Detail
                    </Button>
                ) : (
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            form="account-form"
                            disabled={isLoading}
                        >
                            <Save className="mr-2 h-4 w-4" />
                            {isLoading ? <LoaderCircle className="animate-spin" /> : 'Save Changes'}
                        </Button>
                    </div>
                )}
            </div>
            <div className='mt-[16px] mb-[16px]'>
                {
                    isError && <ErrorComponent message={isAxiosError ? error?.response?.data?.error : ''} />
                }
            </div>

            <form id="account-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Profile Details */}
                <Card>
                    <CardHeader className="hover:bg-gray-50 transition-colors">
                        <CardTitle className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Profile Details
                            </div>
                        </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-6">
                        {isEditMode ? (
                            <>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">Profile Picture</label>
                                    <div className='w-45 h-45'>
                                        <MediaUploader
                                            handleSendToUpload={async (files) => {
                                                const urls = await handleSendToUpload(files);
                                                setValue('profile.profilePicture', urls[0] || '');
                                            }}
                                            onRemove={() => setValue('profile.profilePicture', '')}
                                            value={formData?.profile?.profilePicture || ''}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        value={formData?.profile?.fullName || ''}
                                        onChange={(e) => setValue('profile.fullName', e.target.value)}
                                        placeholder="Enter your full name"
                                        disabled={isAdmin}
                                        maxLength={100}
                                        className={errors.profile?.fullName ? "border-red-500" : ""}
                                    />
                                    {errors.profile?.fullName && (
                                        <p className="text-sm text-red-600">{errors.profile.fullName.message}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        type="email"
                                        value={formData?.profile?.email || ''}
                                        onChange={(e) => setValue('profile.email', e.target.value)}
                                        placeholder="Enter your email address"
                                        disabled={true}
                                        maxLength={255}
                                        className={errors.profile?.email ? "border-red-500 text-gray-500 bg-gray-50 cursor-not-allowed" : "text-gray-500 bg-gray-50 cursor-not-allowed"}
                                    />
                                    {errors.profile?.email && (
                                        <p className="text-sm text-red-600">{errors.profile.email.message}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        Phone Number <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        type="tel"
                                        value={formData?.profile?.phoneNumber || ''}
                                        onChange={(e) => setValue('profile.phoneNumber', e.target.value)}
                                        placeholder="Enter your phone number"
                                        disabled={true}
                                        maxLength={20}
                                        className={errors.profile?.phoneNumber ? "border-red-500 text-gray-500 bg-gray-50 cursor-not-allowed" : "text-gray-500 bg-gray-50 cursor-not-allowed"}
                                    />
                                    {errors.profile?.phoneNumber && (
                                        <p className="text-sm text-red-600">{errors.profile.phoneNumber.message}</p>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">Profile Picture</label>
                                    <div className="flex items-center w-45 h-45">
                                        {formData?.profile?.profilePicture ? (
                                            <img
                                                src={normalizeUrl(formData.profile.profilePicture)}
                                                alt="Profile"
                                                className="w-full h-full object-cover rounded border"
                                            />
                                        ) : (
                                            <div className="w-full h-full border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
                                                <User className="w-full h-full text-gray-400" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">Full Name</label>
                                    <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                        {formData?.profile?.fullName || 'Not provided'}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">Email</label>
                                    <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                        {formData?.profile?.email || 'Not provided'}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">Phone Number</label>
                                    <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                        {formData?.profile?.phoneNumber || 'Not provided'}
                                    </div>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {
                    !isAdmin && <Card>
                        <CardHeader className="hover:bg-gray-50 transition-colors">
                            <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Building className="h-5 w-5" />
                                    Business Details
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {isEditMode ? (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700">Company Logo</label>
                                        <div className='w-45 mt-2 h-45'>
                                            <MediaUploader
                                                handleSendToUpload={async (files) => {
                                                    const urls = await handleSendToUpload(files);
                                                    setValue('business.companyLogo', urls[0] || '');
                                                }}
                                                onRemove={() => setValue('business.companyLogo', '')}
                                                value={formData?.business?.companyLogo || ''}
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700">
                                            Business Name <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            value={formData?.business?.businessName || ''}
                                            onChange={(e) => setValue('business.businessName', e.target.value)}
                                            placeholder="Enter your business name"
                                            maxLength={100}
                                            className={errors.business?.businessName ? "border-red-500" : ""}
                                        />
                                        {errors.business?.businessName && (
                                            <p className="text-sm text-red-600">{errors.business.businessName.message}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700">
                                            Phone Number <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            type="tel"
                                            value={formData?.business?.phoneNumber || ''}
                                            onChange={(e) => setValue('business.phoneNumber', e.target.value)}
                                            placeholder="Enter business phone number"
                                            maxLength={20}
                                            className={errors.business?.phoneNumber ? "border-red-500" : ""}
                                        />
                                        {errors.business?.phoneNumber && (
                                            <p className="text-sm text-red-600">{errors.business.phoneNumber.message}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700">Website</label>
                                        <Input
                                            type="url"
                                            value={formData?.business?.website || ''}
                                            onChange={(e) => setValue('business.website', e.target.value)}
                                            placeholder="Enter your website URL"
                                            maxLength={255}
                                            className={errors.business?.website ? "border-red-500" : ""}
                                        />
                                        {errors.business?.website && (
                                            <p className="text-sm text-red-600">{errors.business.website.message}</p>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700">Company Logo</label>
                                        <div className="flex items-center w-45 mt-2 h-45">
                                            {formData?.business?.companyLogo ? (
                                                <img
                                                    src={normalizeUrl(formData.business.companyLogo)}
                                                    alt="Company Logo"
                                                    className="w-full h-full object-cover rounded border"
                                                />
                                            ) : (
                                                <div className="w-full h-full border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
                                                    <Building className="h-6 w-6 text-gray-400" />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700">Business Name</label>
                                        <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                            {formData?.business?.businessName || 'Not provided'}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700">Phone Number</label>
                                        <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                            {formData?.business?.phoneNumber || 'Not provided'}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700">Website</label>
                                        <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                            {formData?.business?.website || 'Not provided'}
                                        </div>
                                    </div>
                                </>
                            )}

                            <Separator />

                            <div className="space-y-4">
                                <h4 className="font-medium text-gray-900">Business Address</h4>

                                {isEditMode ? (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">
                                                Address Line 1 <span className="text-red-500">*</span>
                                            </label>
                                            <Input
                                                value={formData?.business?.addressLine1 || ''}
                                                onChange={(e) => setValue('business.addressLine1', e.target.value)}
                                                placeholder="Enter street address"
                                                maxLength={255}
                                                className={errors.business?.addressLine1 ? "border-red-500" : ""}
                                            />
                                            {errors.business?.addressLine1 && (
                                                <p className="text-sm text-red-600">{errors.business.addressLine1.message}</p>
                                            )}
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">Address Line 2</label>
                                            <Input
                                                value={formData?.business?.addressLine2 || ''}
                                                onChange={(e) => setValue('business.addressLine2', e.target.value)}
                                                placeholder="Apartment, suite, etc. (optional)"
                                                maxLength={255}
                                                className={errors.business?.addressLine2 ? "border-red-500" : ""}
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">
                                                    City <span className="text-red-500">*</span>
                                                </label>
                                                <Input
                                                    value={formData?.business?.city || ''}
                                                    onChange={(e) => setValue('business.city', e.target.value)}
                                                    placeholder="Enter city"
                                                    maxLength={100}
                                                    className={errors.business?.city ? "border-red-500" : ""}
                                                />
                                                {errors.business?.city && (
                                                    <p className="text-sm text-red-600">{errors.business.city.message}</p>
                                                )}
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">
                                                    State <span className="text-red-500">*</span>
                                                </label>
                                                <Input
                                                    value={formData?.business?.state || ''}
                                                    onChange={(e) => setValue('business.state', e.target.value)}
                                                    placeholder="Enter state"
                                                    maxLength={100}
                                                    className={errors.business?.state ? "border-red-500" : ""}
                                                />
                                                {errors.business?.state && (
                                                    <p className="text-sm text-red-600">{errors.business.state.message}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">
                                                Zip Code <span className="text-red-500">*</span>
                                            </label>
                                            <Input
                                                value={formData?.business?.zipCode || ''}
                                                onChange={(e) => setValue('business.zipCode', e.target.value.replace(/\D/g, ''))}
                                                placeholder="Enter zip code"
                                                maxLength={10}
                                                className={errors.business?.zipCode ? "border-red-500" : ""}
                                            />
                                            {errors.business?.zipCode && (
                                                <p className="text-sm text-red-600">{errors.business.zipCode.message}</p>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">Address Line 1</label>
                                            <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                                {formData?.business?.addressLine1 || 'Not provided'}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">Address Line 2</label>
                                            <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                                {formData?.business?.addressLine2 || 'Not provided'}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">City</label>
                                                <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                                    {formData?.business?.city || 'Not provided'}
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">State</label>
                                                <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                                    {formData?.business?.state || 'Not provided'}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">Zip Code</label>
                                            <div className="text-sm text-gray-900 py-2 px-3 bg-gray-50 rounded border min-h-[36px] flex items-center">
                                                {formData?.business?.zipCode || 'Not provided'}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                }

            </form>
        </div>
    );
}